<template>
  <div class="rounded-xl p-4 transition-colors"
       style="background: rgba(248,113,113,0.06); border: 1px solid rgba(248,113,113,0.2)">
    <!-- Main Deduction Row -->
    <div
      class="flex items-center justify-between gap-3"
      :class="{ 'cursor-pointer': hasDetails }"
      @click="toggleExpanded"
    >
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <h4 class="text-sm font-semibold text-slate-200">{{ title }}</h4>
          <span v-if="warning" class="ep-badge ep-badge-warning" :title="warning">
            ⚠️ {{ warning }}
          </span>
        </div>
        <p v-if="hasDetails" class="text-xs text-slate-500 mt-1">{{ detailsSummary }}</p>
      </div>

      <div class="flex items-center gap-2.5 shrink-0">
        <span class="text-base font-semibold text-red-400 tabular-nums">
          −{{ formatCurrency(amount) }}
        </span>
        <button
          v-if="hasDetails"
          class="p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors"
          @click.stop="toggleExpanded"
        >
          <svg
            class="w-5 h-5 transform transition-transform duration-200"
            :class="{ 'rotate-180': isExpanded }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Expandable Details -->
    <div
      v-if="hasDetails && isExpanded"
      class="mt-4 pt-4 space-y-2"
      style="border-top: 1px solid rgba(248,113,113,0.18)"
    >
      <!-- Absent Days Details -->
      <div v-if="type === 'absent_days' && details?.dates?.length > 0">
        <h5 class="text-xs font-semibold text-slate-300 mb-2">Absent Days ({{ details.dates.length }})</h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div
            v-for="(date, index) in details.dates"
            :key="index"
            class="ded-cell"
          >
            <div class="font-medium text-slate-200">{{ formatDate(date) }}</div>
            <div class="text-slate-500">Full day deduction</div>
          </div>
        </div>
        <div class="mt-3 ded-cell">
          <span class="text-slate-400">Calculation:</span>
          <span class="font-medium text-slate-200 ml-2">
            {{ details.dates.length }} days × {{ formatCurrency(details.hourly_rate) }}/hour = {{ formatCurrency(amount) }}
          </span>
        </div>
      </div>

      <!-- Late Arrivals Details -->
      <div v-if="type === 'late_arrivals' && details?.incidents?.length > 0">
        <h5 class="text-xs font-semibold text-slate-300 mb-2">Late Arrivals ({{ details.incidents.length }})</h5>
        <div class="space-y-2">
          <div
            v-for="(incident, index) in details.incidents"
            :key="index"
            class="ded-cell"
          >
            <div class="flex justify-between">
              <span class="font-medium text-slate-200">{{ formatDate(incident.date) }}</span>
              <span class="text-red-400 tabular-nums">−{{ formatCurrency(incident.deduction) }}</span>
            </div>
            <div class="text-slate-500 mt-1">
              Late by {{ incident.minutes }} minutes
            </div>
          </div>
        </div>
        <div class="mt-3 ded-cell">
          <span class="text-slate-400">Total deduction:</span>
          <span class="font-medium text-red-400 ml-2 tabular-nums">{{ formatCurrency(amount) }}</span>
        </div>
      </div>

      <!-- Short Leave Details -->
      <div v-if="type === 'short_leave' && details?.sessions?.length > 0">
        <h5 class="text-xs font-semibold text-slate-300 mb-2">Short Leave ({{ details.total_hours }} hours)</h5>
        <div class="space-y-2">
          <div
            v-for="(session, index) in details.sessions"
            :key="index"
            class="ded-cell"
          >
            <div class="flex justify-between">
              <span class="font-medium text-slate-200">{{ formatDate(session.date) }}</span>
              <span class="text-red-400 tabular-nums">−{{ formatCurrency(session.deduction) }}</span>
            </div>
            <div class="text-slate-500 mt-1">
              {{ session.hours }} hours ({{ session.start_time }} - {{ session.end_time }})
            </div>
          </div>
        </div>
        <div class="mt-3 ded-cell">
          <span class="text-slate-400">Calculation:</span>
          <span class="font-medium text-slate-200 ml-2">
            {{ details.total_hours }} hours × {{ formatCurrency(details.hourly_rate) }}/hour = {{ formatCurrency(amount) }}
          </span>
        </div>
      </div>

      <!-- Early Departure Details -->
      <div v-if="type === 'early_departure' && details?.incidents?.length > 0">
        <h5 class="text-xs font-semibold text-slate-300 mb-2">Early Departures ({{ details.incidents.length }})</h5>
        <div class="space-y-2">
          <div
            v-for="(incident, index) in details.incidents"
            :key="index"
            class="ded-cell"
          >
            <div class="flex justify-between">
              <span class="font-medium text-slate-200">{{ formatDate(incident.date) }}</span>
              <span class="text-red-400 tabular-nums">−{{ formatCurrency(incident.deduction) }}</span>
            </div>
            <div class="text-slate-500 mt-1">
              Left {{ incident.minutes }} minutes early
            </div>
          </div>
        </div>
        <div class="mt-3 ded-cell">
          <span class="text-slate-400">Total deduction:</span>
          <span class="font-medium text-red-400 ml-2 tabular-nums">{{ formatCurrency(amount) }}</span>
        </div>
      </div>

      <!-- Emergency Leave Details -->
      <div v-if="type === 'emergency_leave' && details?.days?.length > 0">
        <h5 class="text-xs font-semibold text-slate-300 mb-2">Emergency Leave ({{ details.days.length }} days)</h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div
            v-for="(day, index) in details.days"
            :key="index"
            class="ded-cell"
          >
            <div class="font-medium text-slate-200">{{ formatDate(day.date) }}</div>
            <div class="text-slate-500">{{ day.reason || 'Emergency leave' }}</div>
          </div>
        </div>
        <div class="mt-3 ded-cell">
          <span class="text-slate-400">Calculation:</span>
          <span class="font-medium text-slate-200 ml-2">
            {{ details.days.length }} days × {{ formatCurrency(details.daily_rate) }}/day = {{ formatCurrency(amount) }}
          </span>
        </div>
      </div>

      <!-- Manual Adjustments (No breakdown needed) -->
      <div v-if="type === 'manual_adjustment' && details?.description">
        <div class="ded-cell">
          <div class="text-slate-300">{{ details.description }}</div>
          <div v-if="details.approved_by" class="text-slate-500 mt-1">
            Approved by: {{ details.approved_by }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useCurrency } from '@/composables/useCurrency';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  amount: {
    type: Number,
    required: true
  },
  details: {
    type: Object,
    default: null
  },
  type: {
    type: String,
    required: true
  },
  warning: {
    type: String,
    default: null
  }
});

const { formatCurrency } = useCurrency();
const isExpanded = ref(false);

const hasDetails = computed(() => {
  if (!props.details) return false;

  switch (props.type) {
    case 'absent_days':
      return props.details.dates && props.details.dates.length > 0;
    case 'late_arrivals':
      return props.details.incidents && props.details.incidents.length > 0;
    case 'short_leave':
      return props.details.sessions && props.details.sessions.length > 0;
    case 'early_departure':
      return props.details.incidents && props.details.incidents.length > 0;
    case 'emergency_leave':
      return props.details.days && props.details.days.length > 0;
    case 'manual_adjustment':
      return !!props.details.description;
    default:
      return false;
  }
});

const detailsSummary = computed(() => {
  if (!props.details) return '';

  switch (props.type) {
    case 'absent_days':
      return `${props.details.dates?.length || 0} days`;
    case 'late_arrivals':
      return `${props.details.incidents?.length || 0} incidents`;
    case 'short_leave':
      return `${props.details.total_hours || 0} hours`;
    case 'early_departure':
      return `${props.details.incidents?.length || 0} incidents`;
    case 'emergency_leave':
      return `${props.details.days?.length || 0} days`;
    case 'manual_adjustment':
      return props.details.description || '';
    default:
      return '';
  }
});

function toggleExpanded() {
  if (hasDetails.value) {
    isExpanded.value = !isExpanded.value;
  }
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
</script>

<style scoped>
.ded-cell {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
}
</style>
